import { type FunctionComponent, type HTMLAttributes, useEffect } from 'react';
import { type PartialPick } from '../../../../../@types/build';
import { generateDataSelector } from '../../../helpers/general-helper/general-helper';
import { useExpandableBehavior } from '../../../hooks/expandable/expandable.hooks';
import { ClickableElement } from '../../buttons';
import ChevronDownIcon from '../../svgs/untitled-ui/chevron-down.svg';
import ChevronUpIcon from '../../svgs/untitled-ui/chevron-up.svg';
import MinusIcon from '../../svgs/untitled-ui/minus.svg';
import PlusIcon from '../../svgs/untitled-ui/plus.svg';
import { Badge } from '../badge/badge.component';

type IndicatorPositions = 'left' | 'right';
type IndicatorStyles = 'plus' | 'chevron';
type IndicatorColors = 'white' | 'black' | 'grey';

export type ExpandablePanelProps = {
	children: JSX.Element | JSX.Element[];
	containerClassName?: string;
	heading?: string | JSX.Element;
	forceExpand?: boolean;
	startExpanded?: boolean;
	onExpand?: Function;
	onCollapse?: Function;
	renderCollapsedChildren?: boolean;
	borderRadius?: string;
	badgeCount?: number;
	automationHook?: string;
	indicatorPosition?: IndicatorPositions;
	indicatorStyle?: IndicatorStyles;
	indicatorColor?: IndicatorColors;
	indicatorSize?: string;
	forceClose?: boolean;
	expandedBorder?: boolean;
	disabled?: boolean;
	lightBorder?: boolean;
	headerClassName?: string;
} & PartialPick<HTMLAttributes<HTMLElement>, 'id' | 'className'>;

const EXPAND_INDICATORS_MAP: Readonly<{ [key in IndicatorStyles]: React.ReactNode }> = {
	plus: <PlusIcon />,
	chevron: <ChevronDownIcon className="text-xl -my-1.5 -mr-1.5" />
};

const COLLAPSE_INDICATORS_MAP: Readonly<{ [key in IndicatorStyles]: React.ReactNode }> = {
	chevron: <ChevronUpIcon className="text-xl -my-1.5 -mr-1.5" />,
	plus: <MinusIcon />
};

const Indicator: FunctionComponent<{ indicatorStyle: IndicatorStyles; isExpanded: boolean; indicatorSize: string }> = ({
	indicatorStyle,
	isExpanded,
	indicatorSize
}) => {
	const expandIcon = EXPAND_INDICATORS_MAP[indicatorStyle];
	const collapseIcon = COLLAPSE_INDICATORS_MAP[indicatorStyle];

	return (
		// TODO: Figure out how to tie icon size to font size for better scaling with text size changes
		<div data-testid="indicator" className={`f-inherit lh-solid ml3 flex items-center ${indicatorSize}`} data-automation="expand">
			{isExpanded ? collapseIcon : expandIcon}
		</div>
	);
};

export const ExpandablePanel: FunctionComponent<ExpandablePanelProps> = ({
	children,
	id,
	expandedBorder = true,
	className = 'f3',
	containerClassName = 'pa3',
	renderCollapsedChildren = false,
	heading,
	onExpand,
	onCollapse,
	borderRadius,
	badgeCount = 0,
	forceExpand = false,
	startExpanded = false,
	automationHook,
	indicatorPosition = 'left',
	indicatorStyle = 'plus',
	indicatorColor = 'black',
	indicatorSize = 'text-2xl',
	forceClose = false,
	disabled = false,
	lightBorder = false,
	headerClassName = ''
}) => {
	const { isExpanded, toggle, handleKeyPress, handleClick } = useExpandableBehavior({
		startExpanded,
		onExpand,
		onCollapse
	});

	const childrenDisplayClass = !isExpanded ? 'dn' : `db ${containerClassName}`;
	const shouldRenderChildren = isExpanded || renderCollapsedChildren;

	useEffect(() => {
		if (forceExpand && !isExpanded) {
			toggle();
		}
		if (forceClose && isExpanded) {
			toggle();
		}
	}, [forceExpand, forceClose, isExpanded, toggle]);

	return (
		<section
			id={id}
			className={`w-100 ba ${borderRadius ? borderRadius : 'br1'} ${
				lightBorder ? 'b--theme-grey-lighter' : 'b--theme-grey-light'
			} ${className}`}
			role="tabpanel"
			data-automation={generateDataSelector('panel', automationHook)}>
			<ClickableElement
				className={`flex ${
					indicatorPosition === 'right' ? 'justify-between mh2 pl0 pr3' : ''
				} items-center pv3 w-100 f-inherit theme-${indicatorColor}`}
				onClick={handleClick}
				tabIndex={0}
				onKeyDown={handleKeyPress}
				ariaLabel={`Expand ${heading} Section`}
				disabled={disabled}>
				{indicatorPosition === 'right' ? (
					<>
						<div className="flex items-center">
							<h2 className={`mv0 mh2 lh-title f-inherit ${headerClassName}`}>{heading}</h2>
							<Badge count={badgeCount} />
						</div>
						<div className="mr2">
							<Indicator isExpanded={isExpanded} indicatorStyle={indicatorStyle} indicatorSize={indicatorSize} />
						</div>
					</>
				) : (
					<>
						<Indicator isExpanded={isExpanded} indicatorStyle={indicatorStyle} indicatorSize={indicatorSize} />
						<h2 className={`mv0 mh2 lh-title f-inherit ${headerClassName}`}>{heading}</h2>
						<Badge count={badgeCount} />
					</>
				)}
			</ClickableElement>
			{isExpanded && <div className={`mh3 ${expandedBorder ? 'bb b--theme-grey-light' : ''}`} />}
			<div className={childrenDisplayClass}>{shouldRenderChildren && children}</div>
		</section>
	);
};
